import { useEffect, useMemo, useState } from "react";
import { FaRegEdit } from "react-icons/fa";
import { ClockIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import StructuredTableSkeleton from "@/app/components/ui/Skeletons/StructuredTableSkeleton";
import TableComponent, { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import { IStoreTiming } from "./interface";
import { format12HourTime } from "./helpers";
import { useStoreTimingAPI } from "./useStoreTimingAPI";
import StoreTimingModal from "./StoreTimingModal";

const StatusBadge = ({ isActive }: { isActive?: boolean }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold sm:px-2.5 sm:text-xs ${
      isActive ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-gray-200 bg-gray-100 text-gray-500"
    }`}
  >
    <span className={`h-1.5 w-1.5 rounded-full ${isActive ? "bg-emerald-500" : "bg-gray-400"}`} />
    {isActive ? "Active" : "Inactive"}
  </span>
);

const StoreTimingSetup = () => {
  const { storeTimingList, isListLoading, isSaving, fetchStoreTimingList, saveStoreTiming } = useStoreTimingAPI();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [editingTiming, setEditingTiming] = useState<IStoreTiming | null>(null);

  useEffect(() => {
    fetchStoreTimingList();
  }, [fetchStoreTimingList]);

  const visibleList = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    if (!search) return storeTimingList;
    return storeTimingList.filter((timing) => timing.warehouse_name?.toLowerCase().includes(search));
  }, [storeTimingList, searchTerm]);

  const columns = useMemo<TableColumn<IStoreTiming>[]>(
    () => [
      {
        id: "warehouse_name",
        header: "Quick Commerce Store",
        accessor: (row) => row.warehouse_name || "—",
        className: "font-semibold text-gray-900 text-xs 2xl:text-sm",
        headerClassName: "text-[#193A6A]",
        width: "34%",
        minWidth: 200,
      },
      {
        id: "is_active",
        header: "Status",
        cell: ({ row }) => <StatusBadge isActive={row.is_active} />,
        headerClassName: "text-[#193A6A]",
        width: "16%",
        minWidth: 110,
      },
      {
        id: "opening_time",
        header: "Opening Time",
        accessor: (row) => format12HourTime(row.opening_hour, row.opening_minute),
        className: "text-gray-700 tabular-nums whitespace-nowrap text-xs 2xl:text-sm",
        headerClassName: "text-[#193A6A]",
        width: "19%",
        minWidth: 130,
      },
      {
        id: "closing_time",
        header: "Closing Time",
        accessor: (row) => format12HourTime(row.closing_hour, row.closing_minute),
        className: "text-gray-700 tabular-nums whitespace-nowrap text-xs 2xl:text-sm",
        headerClassName: "text-[#193A6A]",
        width: "19%",
        minWidth: 130,
      },
      {
        id: "edit",
        header: "Action",
        align: "center",
        cell: ({ row }) => (
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={() => setEditingTiming(row)}
              aria-label={`Edit timing for ${row.warehouse_name || row.warehouse_id}`}
              title="Edit timing"
              className="rounded-lg p-1.5 text-[#193A6A] transition-colors hover:bg-[#EEF5FF] hover:text-[#132c52] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#193A6A]/40"
            >
              <FaRegEdit className="h-4 w-4" />
            </button>
          </div>
        ),
        width: "12%",
        minWidth: 90,
        headerClassName: "text-[#193A6A] text-center",
      },
    ],
    [],
  );

  return (
    <div className="px-4 py-3 sm:px-5">
      <div className="min-w-0">
        <h1 className="text-sm font-semibold leading-6 text-gray-900 sm:text-base">Quick Commerce Timings</h1>
        <p className="mt-1 max-w-2xl text-[11px] leading-5 text-gray-500 sm:mt-2 sm:text-xs 2xl:text-sm">
          Set the opening and closing time for each quick commerce store.
        </p>
      </div>

      <div className="relative mt-4 w-full sm:mt-5 sm:w-[440px]">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search Quick Commerce Store"
          aria-label="Search quick commerce store by name"
          className="h-11 w-full rounded-lg border border-gray-300 pl-9 pr-3 text-base text-slate-700 placeholder:text-gray-400 transition-colors focus:border-[#193A6A] focus:outline-none focus:ring-2 focus:ring-[#193A6A]/15 sm:h-[38px] sm:text-xs"
        />
      </div>

      {isListLoading ? (
        <div className="mt-5">
          <div className="hidden md:block">
            <StructuredTableSkeleton columns={columns.length} />
          </div>
          <div className="md:hidden">
            <StructuredTableSkeleton columns={2} rows={4} />
          </div>
        </div>
      ) : visibleList.length === 0 ? (
        <NoDataFoundComponent message="No quick commerce stores found" description="Try changing the search term." />
      ) : (
        <>
          <div className="mt-5 hidden md:block">
            <TableComponent<IStoreTiming>
              columns={columns}
              data={visibleList}
              getRowId={(row) => row.warehouse_id}
              containerClassName="mr-0 rounded-2xl border border-gray-200 bg-white shadow-sm"
              scrollContainerClassName="overflow-x-auto mt-0 max-h-[calc(100vh-200px)]"
              minTableWidthClassName="min-w-full"
              tableClassName="min-w-full divide-y divide-gray-200"
              stickyHeader={true}
              wrapCells={true}
            />
          </div>

          <p className="mt-4 text-[11px] text-gray-500 md:hidden">
            Showing <span className="font-semibold text-gray-700">{visibleList.length}</span> of{" "}
            {storeTimingList.length} quick commerce stores
          </p>

          <ul className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 md:hidden">
            {visibleList.map((timing) => (
              <li
                key={timing.warehouse_id}
                className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
              >
                <div className="flex items-start gap-2 p-4 pb-3">
                  <p className="min-w-0 flex-1 break-words text-[13px] font-semibold leading-5 text-gray-900">
                    {timing.warehouse_name || "—"}
                  </p>
                  <StatusBadge isActive={timing.is_active} />
                </div>

                <div className="mx-4 flex items-center gap-2.5 rounded-xl bg-slate-50 px-3 py-2.5">
                  <ClockIcon className="h-4 w-4 shrink-0 text-[#193A6A]" />
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <div className="min-w-0">
                      <p className="text-[9px] font-medium uppercase tracking-wide text-gray-400">Opens</p>
                      <p className="text-xs font-semibold tabular-nums text-gray-800">
                        {format12HourTime(timing.opening_hour, timing.opening_minute)}
                      </p>
                    </div>
                    <span className="shrink-0 text-gray-300">&rarr;</span>
                    <div className="min-w-0">
                      <p className="text-[9px] font-medium uppercase tracking-wide text-gray-400">Closes</p>
                      <p className="text-xs font-semibold tabular-nums text-gray-800">
                        {format12HourTime(timing.closing_hour, timing.closing_minute)}
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setEditingTiming(timing)}
                  aria-label={`Edit timing for ${timing.warehouse_name || timing.warehouse_id}`}
                  className="mt-3 flex min-h-[44px] items-center justify-center gap-2 border-t border-gray-100 text-xs font-semibold text-[#193A6A] transition-colors active:bg-[#EEF5FF]"
                >
                  <FaRegEdit className="h-3.5 w-3.5" />
                  Edit Timing
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <StoreTimingModal
        timing={editingTiming}
        isSaving={isSaving}
        onClose={() => setEditingTiming(null)}
        onSave={saveStoreTiming}
      />
    </div>
  );
};

export default StoreTimingSetup;
